import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GastoPlan, PagoPlan, Plan } from '../types';
import { useAuth } from './AuthContext';
import { useAMedias, useMovimientosPlan } from './AMediasContext';
import { useSincronizacionPlan } from '../hooks/useSincronizacionPlan';
import { calcularSaldos, simplificarDeudas, Transferencia } from '../utils/amediasUtils';
import { origenesAfectados } from '../utils/sincronizacionAMedias';
import { estadoRecordatorio, pospuestoHasta, posponerRecordatorio } from '../utils/recordatoriosAMedias';

/**
 * Vigila todos los planes de A medias del usuario mientras la app está
 * abierta, para que los avisos salgan nada más entrar (Inicio, A medias,
 * Finanzas) y no solo dentro de cada plan:
 *
 * - deudas cuyo plazo de recordatorio ya ha pasado;
 * - movimientos que apuntaron otros y esperan para pasar a Finanzas.
 *
 * También es quien aplica la sincronización automática con Finanzas: así
 * ocurre al abrir la app, y una sola vez (la página del plan ya no lo hace).
 */

export interface EstadoPlan {
  cargando: boolean;
  gastos: GastoPlan[];
  pagos: PagoPlan[];
  miSaldo: number;
  /** Lo que debes, si ya pasó el plazo de recordatorio y no lo has pospuesto. */
  deudasVencidas: Transferencia[];
  diasDesdeReferencia: number;
  /** Gastos o pagos de otros pendientes de confirmar para Finanzas. */
  pendientesFinanzas: number;
  errorFinanzas: string;
}

export type Aviso =
  | { tipo: 'deuda'; plan: Plan; deudas: Transferencia[]; dias: number }
  | { tipo: 'finanzas'; plan: Plan; cuantos: number }
  | { tipo: 'errorFinanzas'; plan: Plan; mensaje: string };

interface AvisosAMediasContextType {
  estadoPlan: (planId: string) => EstadoPlan | undefined;
  avisos: Aviso[];
  pasarAFinanzas: (planId: string) => Promise<void>;
  /** Tras un error de sincronización corregido (p. ej. otra cuenta), volver a intentarlo. */
  reintentarFinanzas: (planId: string) => void;
  posponer: (planId: string) => void;
}

interface Acciones { pasar: () => Promise<void>; reintentar: () => void }

const Ctx = createContext<AvisosAMediasContextType | undefined>(undefined);

export const AvisosAMediasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { planes } = useAMedias();
  const [estados, setEstados] = useState<Record<string, EstadoPlan>>({});
  const acciones = useRef<Record<string, Acciones>>({});
  // Posponer vive en localStorage: este contador obliga a recalcular
  const [vuelta, setVuelta] = useState(0);

  const informar = useCallback((planId: string, e: EstadoPlan, a: Acciones) => {
    acciones.current[planId] = a;
    setEstados(prev => (igual(prev[planId], e) ? prev : { ...prev, [planId]: e }));
  }, []);

  // Olvidar los planes que ya no están (borrados, o de los que saliste)
  useEffect(() => {
    setEstados(prev => {
      const vivos = Object.fromEntries(Object.entries(prev).filter(([id]) => planes.some(p => p.id === id)));
      return Object.keys(vivos).length === Object.keys(prev).length ? prev : vivos;
    });
  }, [planes]);

  const avisos = useMemo(() => {
    const out: Aviso[] = [];
    planes.forEach(plan => {
      const e = estados[plan.id];
      if (!e) return;
      if (e.errorFinanzas) out.push({ tipo: 'errorFinanzas', plan, mensaje: e.errorFinanzas });
      else if (e.pendientesFinanzas > 0) out.push({ tipo: 'finanzas', plan, cuantos: e.pendientesFinanzas });
      if (e.deudasVencidas.length > 0) out.push({ tipo: 'deuda', plan, deudas: e.deudasVencidas, dias: e.diasDesdeReferencia });
    });
    return out;
  }, [planes, estados]);

  const valor: AvisosAMediasContextType = {
    estadoPlan: id => estados[id],
    avisos,
    pasarAFinanzas: id => acciones.current[id]?.pasar() ?? Promise.resolve(),
    reintentarFinanzas: id => acciones.current[id]?.reintentar(),
    posponer: id => {
      const plan = planes.find(p => p.id === id);
      posponerRecordatorio(id, Math.min(plan?.recordatorioDias || 7, 7));
      setVuelta(v => v + 1);
    },
  };

  return (
    <Ctx.Provider value={valor}>
      {planes.map(p => <VigiaPlan key={p.id} plan={p} vuelta={vuelta} informar={informar} />)}
      {children}
    </Ctx.Provider>
  );
};

/** Sin interfaz: carga un plan, sincroniza con Finanzas y calcula sus avisos. */
const VigiaPlan: React.FC<{
  plan: Plan;
  vuelta: number;
  informar: (planId: string, e: EstadoPlan, a: Acciones) => void;
}> = ({ plan, vuelta, informar }) => {
  const { user } = useAuth();
  const { gastos, pagos, cargando } = useMovimientosPlan(plan.id);
  const sync = useSincronizacionPlan(plan, gastos, pagos, !cargando, true);
  const yo = plan.participantes.find(p => !!user && p.uid === user.uid);

  const estado = useMemo<EstadoPlan>(() => {
    const saldos = calcularSaldos(gastos, pagos);
    const misDeudas = yo ? simplificarDeudas(saldos).filter(d => d.de === yo.id) : [];
    const rec = estadoRecordatorio(plan, gastos);
    const avisar = !cargando && rec.vencido && pospuestoHasta(plan.id) < Date.now();
    return {
      cargando, gastos, pagos,
      miSaldo: yo ? saldos[yo.id] ?? 0 : 0,
      deudasVencidas: avisar ? misDeudas : [],
      diasDesdeReferencia: rec.dias,
      pendientesFinanzas: origenesAfectados(sync.pendientes),
      errorFinanzas: sync.error,
    };
    // `vuelta` fuerza el recálculo al posponer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando, gastos, pagos, yo, plan, sync.pendientes, sync.error, vuelta]);

  useEffect(() => {
    informar(plan.id, estado, { pasar: sync.pasarPendientes, reintentar: sync.reintentar });
  });
  return null;
};

/** Para no repintar toda la app si un plan recalcula y no cambia nada visible. */
function igual(a: EstadoPlan | undefined, b: EstadoPlan) {
  return !!a && a.cargando === b.cargando && a.gastos === b.gastos && a.pagos === b.pagos
    && a.miSaldo === b.miSaldo && a.diasDesdeReferencia === b.diasDesdeReferencia
    && a.pendientesFinanzas === b.pendientesFinanzas && a.errorFinanzas === b.errorFinanzas
    && JSON.stringify(a.deudasVencidas) === JSON.stringify(b.deudasVencidas);
}

export const useAvisosAMedias = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAvisosAMedias debe usarse dentro de AvisosAMediasProvider');
  return ctx;
};
